import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Camera, Keyboard, CheckCircle, Warning } from '@phosphor-icons/react';

/**
 * Barcode scanner modal voor Verkoop onderweg.
 * - Start automatisch de camera (achter-camera bij voorkeur, voor tablets)
 * - Detecteert EAN-13, EAN-8, CODE-128, UPC-A/E
 * - Roept onScan(code) aan zodra een scan succesvol is
 * - Toont ook een handmatig-input veld (fallback voor typefouten / slechte camera)
 */
export default function BarcodeScanModal({ open, onClose, onScan, laatsteScan }) {
  const scannerDivId = 'barcode-reader-container';
  const scannerRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [cameras, setCameras] = useState([]);
  const [selectedCam, setSelectedCam] = useState('');
  const [handmatig, setHandmatig] = useState('');

  // Korte beep via WebAudio — geen extra asset nodig
  const beep = (ok = true) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = ok ? 880 : 330;
      o.connect(g); g.connect(ctx.destination);
      g.gain.value = 0.1;
      o.start();
      setTimeout(() => { o.stop(); ctx.close(); }, 120);
    } catch { /* geluid niet kritiek */ }
  };

  // Start scanner op gekozen camera
  const startScan = async (camId) => {
    try {
      setError('');
      if (scannerRef.current) {
        try { await scannerRef.current.stop(); } catch { /* al gestopt */ }
      }
      const html5 = new Html5Qrcode(scannerDivId, { verbose: false });
      scannerRef.current = html5;
      await html5.start(
        camId,
        { fps: 12, qrbox: { width: 280, height: 160 }, aspectRatio: 1.6 },
        (decodedText) => {
          beep(true);
          onScan(decodedText.trim());
        },
        () => { /* ignore per-frame not-found */ }
      );
      setRunning(true);
    } catch (e) {
      setError(e?.message || 'Kan camera niet starten. Geef toestemming in uw browser.');
      setRunning(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const devs = await Html5Qrcode.getCameras();
        if (cancelled) return;
        setCameras(devs || []);
        if (!devs || devs.length === 0) {
          setError('Geen camera gevonden op dit toestel.');
          return;
        }
        // Voorkeur achter-camera (tablet scan)
        const back = devs.find(d => /back|rear|environment|achter/i.test(d.label)) || devs[devs.length - 1];
        setSelectedCam(back.id);
        startScan(back.id);
      } catch (e) {
        setError(e?.message || 'Camera-toegang geweigerd.');
      }
    })();
    return () => {
      cancelled = true;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {}).finally(() => {
          scannerRef.current?.clear?.();
          scannerRef.current = null;
        });
      }
      setRunning(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const changeCamera = async (id) => {
    setSelectedCam(id);
    await startScan(id);
  };

  const submitHandmatig = (e) => {
    e.preventDefault();
    const code = handmatig.trim();
    if (!code) return;
    onScan(code);
    setHandmatig('');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-3" data-testid="scan-modal">
      <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col" style={{ maxHeight: '92vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-[#002FA7] text-white">
          <div className="inline-flex items-center gap-2">
            <Camera size={20} weight="bold" />
            <h3 className="text-base font-bold">Barcode scannen</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10" aria-label="Sluiten" data-testid="scan-sluiten">
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Camera viewport */}
        <div className="relative bg-black" style={{ minHeight: 260 }}>
          <div id={scannerDivId} style={{ width: '100%', minHeight: 260 }} />
          {/* Scanner-overlay frame */}
          {running && !error && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div style={{ width: '70%', height: 140, border: '3px solid #22C55E', boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)' }}>
                <div className="w-full h-0.5 bg-red-500 animate-pulse" style={{ marginTop: 68 }} />
              </div>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-6 text-center">
              <div className="text-white">
                <Warning size={38} className="mx-auto text-amber-400 mb-2" />
                <div className="text-sm">{error}</div>
                <div className="text-[11px] text-gray-300 mt-2">Tip: geef camera-toegang in uw browser of gebruik de handmatige invoer hieronder.</div>
              </div>
            </div>
          )}
        </div>

        {/* Camera-keuze (alleen bij ≥2 cams) */}
        {cameras.length > 1 && (
          <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
            <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-1">Camera</label>
            <select value={selectedCam} onChange={e => changeCamera(e.target.value)} className="field-input w-full" style={{ fontSize: 13 }}>
              {cameras.map(c => <option key={c.id} value={c.id}>{c.label || `Camera ${c.id.slice(0, 6)}`}</option>)}
            </select>
          </div>
        )}

        {/* Laatste succesvolle scan feedback */}
        {laatsteScan && (
          <div className="px-4 py-2 bg-green-50 border-b border-green-200 inline-flex items-center gap-2" data-testid="scan-laatste">
            <CheckCircle size={16} className="text-green-600" weight="fill" />
            <span className="text-xs font-bold text-green-800">{laatsteScan.label}</span>
            <span className="text-[11px] text-green-700 font-mono ml-auto">{laatsteScan.code}</span>
          </div>
        )}

        {/* Handmatige invoer */}
        <form onSubmit={submitHandmatig} className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 inline-flex items-center gap-1 mb-1">
            <Keyboard size={12} /> Handmatig invoeren (EAN/Art-code)
          </label>
          <div className="flex gap-2">
            <input
              className="field-input flex-1"
              value={handmatig}
              onChange={e => setHandmatig(e.target.value)}
              placeholder="bv. 5412345678901"
              inputMode="numeric"
              autoFocus
              style={{ fontSize: 16 }}
              data-testid="scan-handmatig-input"
            />
            <button type="submit" className="btn btn-primary font-bold" style={{ minWidth: 100, minHeight: 44 }} data-testid="scan-handmatig-submit">
              Zoeken
            </button>
          </div>
          <div className="text-[10px] text-gray-500 mt-1">Houd de barcode op ±15 cm van de camera — wordt automatisch herkend.</div>
        </form>

        {/* Close knop */}
        <div className="px-4 py-2 border-t border-gray-200">
          <button onClick={onClose} className="btn btn-secondary w-full font-bold" style={{ minHeight: 48 }} data-testid="scan-stoppen">
            Scanner sluiten
          </button>
        </div>
      </div>
    </div>
  );
}
